/**
 * E2E Flow Export Menu
 * Issue #415: Export dropdown for individual E2E flows
 *
 * Features:
 * - Export to PNG, SVG, JSON formats
 * - Shows export progress
 * - Downloads file with appropriate filename
 */

import { useState, useRef, useEffect } from 'react';
import { api } from '../../services/api';

type ExportFormat = 'png' | 'svg' | 'json';

interface E2EFlowExportMenuProps {
  projectId: string;
  flowId: string;
  flowName: string;
}

const EXPORT_FORMATS: Array<{
  format: ExportFormat;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    format: 'png',
    label: 'PNG Image',
    icon: '🖼️',
    description: 'Raster image for documents',
  },
  {
    format: 'svg',
    label: 'SVG Vector',
    icon: '📐',
    description: 'Scalable vector image',
  },
  {
    format: 'json',
    label: 'JSON Data',
    icon: '📄',
    description: 'Raw flow data',
  },
];

export function E2EFlowExportMenu({
  projectId,
  flowId,
  flowName,
}: E2EFlowExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const blob = await api.exportE2EFlow(projectId, flowId, {
        format,
        includeMetadata: format === 'json',
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Set filename based on format
      const safeName = flowName.replace(/[^a-zA-Z0-9-_]/g, '_');
      const extensions: Record<ExportFormat, string> = {
        png: 'png',
        svg: 'svg',
        json: 'json',
      };
      a.download = `${safeName}.${extensions[format]}`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setIsOpen(false);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Export Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
        title="Export flow"
      >
        {isExporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600" />
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Export</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
          <div className="p-1">
            {EXPORT_FORMATS.map(({ format, label, icon, description }) => (
              <button
                key={format}
                onClick={() => handleExport(format)}
                disabled={isExporting}
                className="w-full flex items-start gap-3 px-3 py-2 text-left rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <span className="text-lg">{icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {label}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {description}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {exportError && (
            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-red-500">{exportError}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default E2EFlowExportMenu;
